// Phase 10A.1 R0/R1 — Reports page mock-data purge.
//
// Removed in this slice:
//   - MOCK_REPORTS (7 fabricated reports including "Weekend Acca
//     Autopsy", "Target Odds Build — Liverpool vs Arsenal",
//     "Council Debate — Man City -1.5 AH", "Weekly Betting
//     Correction — W18" with a fake "ROI: +8.3%" + "Win Rate: 61%"
//     metric and a fake "Sika Goal Progress — April Review"
//     showing GHS 2,400 target / 94% achieved).
//   - MOCK fallback path. Real reports rows surface when present;
//     otherwise the page renders a truthful empty state.
//
// The fictional ROI / Win Rate / Sika progress metrics constituted a
// fabricated public track record — the most severe doctrine breach
// the audit found on this page.

import { Icon } from "../../components/icons/Icon.tsx";
import { useReports } from "../../reports/useReports.ts";
import {
  downloadReportsCsv,
  downloadReportText,
} from "../../reports/reportExport.ts";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";

export function ReportsPage(): JSX.Element {
  const { reports, loading, error } = useReports();

  return (
    <>
      <div className="ex-head">
        <div>
          <h1>Reports</h1>
          <div className="sub">
            Long-form summaries produced by the agent pipeline — slip autopsies, weekly reviews,
            council debates. Read-only.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag">Read-only</span>
          {reports.length > 0 ? (
            <button
              type="button"
              data-testid="reports-export-csv"
              onClick={() => downloadReportsCsv(reports)}
              style={{
                marginLeft: 10,
                padding: "6px 12px",
                border: "1px solid var(--border-faint, #2a2a2a)",
                borderRadius: 4,
                background: "transparent",
                color: "var(--text-secondary, inherit)",
                fontFamily: "var(--font-mono, monospace)",
                fontSize: 11,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Export CSV
            </button>
          ) : null}
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          Reports surface real outputs of the agent pipeline. STRIKZA does not publish a public
          track record, ROI claims, or win-rate boasts.
        </span>
      </div>

      {loading ? (
        <AwaitingDataPanel eyebrow="Reports" title="Loading…" body="Authenticated read pending." />
      ) : error !== null ? (
        <AwaitingDataPanel eyebrow="Reports" title="Reports unavailable." body={error} />
      ) : reports.length === 0 ? (
        <AwaitingDataPanel
          eyebrow="Reports"
          title="No reports yet."
          body={
            <>
              When the agent pipeline produces a report (slip autopsy, weekly review, debate
              transcript) for your account, it appears here. STRIKZA never seeds fabricated reports.
            </>
          }
        />
      ) : (
        <div
          style={{
            marginTop: 24,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {reports.map((r) => (
            <div
              key={r.id}
              style={{
                padding: 16,
                border: "1px solid var(--border-faint, #2a2a2a)",
                borderRadius: 8,
                background: "var(--bg-elev-1, transparent)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono, monospace)",
                  fontSize: 11,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: "var(--text-tertiary, #666)",
                }}
              >
                {r.report_type} · {r.created_at}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-head, inherit)",
                  fontSize: 16,
                  color: "var(--text-primary, inherit)",
                  lineHeight: 1.3,
                }}
              >
                {r.title}
              </span>
              {r.summary !== null ? (
                <p
                  style={{
                    fontFamily: "var(--font-body, inherit)",
                    fontSize: 13,
                    color: "var(--text-secondary, inherit)",
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {r.summary}
                </p>
              ) : null}
              <button
                type="button"
                data-testid={`report-download-${r.id}`}
                onClick={() => downloadReportText(r)}
                style={{
                  alignSelf: "flex-start",
                  marginTop: 4,
                  padding: "5px 10px",
                  border: "1px solid var(--border-faint, #2a2a2a)",
                  borderRadius: 4,
                  background: "transparent",
                  color: "var(--text-secondary, inherit)",
                  fontFamily: "var(--font-mono, monospace)",
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Download
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
